import { useCommandHandler, useWhenContext } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { useUndo } from "@/lib/undo";
import type { useSearchPalette } from "@/features/search/hooks/use-search-palette";
import type { useTheme } from "@/providers/theme-provider";
import type { useLocation, useNavigate } from "@tanstack/solid-router";

interface AppShortcutsOptions {
  searchPalette: ReturnType<typeof useSearchPalette>;
  location: ReturnType<typeof useLocation>;
  navigate: ReturnType<typeof useNavigate>;
  theme: ReturnType<typeof useTheme>;
  shortcutsOpen: () => boolean;
  setShortcutsOpen: (open: boolean) => void;
  setIsSidebarOpen: (f: (prev: boolean) => boolean) => void;
}

export function useAppShortcuts(options: AppShortcutsOptions) {
  // ── Shortcuts: push reactive context flags so the engine can evaluate
  // when-clauses. `dialog.open` is owned by the Dialog primitive / shortcuts
  // modal (the single comprehensive source via the interaction context), so it
  // is intentionally not duplicated here.
  useWhenContext({
    "palette.open": () => options.searchPalette.isOpen(),
  });

  // ── Shortcut handlers (declarative; bindings live in lib/shortcuts/defaults.ts)
  // ── Global undo (L1): Ctrl/Cmd+Z undoes the session's last action ──
  const { undoLast } = useUndo();
  useCommandHandler(COMMAND_IDS.UNDO, () => {
    void undoLast();
  });

  useCommandHandler(COMMAND_IDS.PALETTE_OPEN, () => {
    options.searchPalette.toggle();
  });
  useCommandHandler(COMMAND_IDS.HELP_OPEN, () => {
    options.setShortcutsOpen(true);
  });

  // ── UI toggles ─────────────────────────────────────────────────────────
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_SIDEBAR, () => {
    options.setIsSidebarOpen((prev) => !prev);
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BLUR, () => {
    void options.theme.toggleBlur();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, () => {
    void options.theme.toggleFollowDevice();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE, () => {
    options.theme.toggleBackgroundStyle();
  });
  useCommandHandler(COMMAND_IDS.GOTO_DASHBOARD, () => {
    void options.navigate({ to: "/" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_PROFILE, () => {
    void options.navigate({ to: "/profile" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_THEMES, () => {
    void options.navigate({ to: "/themes" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_SHORTCUTS, () => {
    void options.navigate({ to: "/profile/shortcuts" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_LANGUAGE, () => {
    void options.navigate({ to: "/language" });
  });
}
